/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Import UI component for Sổ Chi Tiết Phát Hành Cổ Phiếu (S43-DN)
import { StockLedgerInfo } from "./info";
import { StockLedgerService } from "services/stock-ledger";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

const createDetailDefault = () => ({
  licenceNumber: "",
  licenceDate: "",
  registeredStockType: "",
  registeredQuantity: "",
  registeredParValue: "",
  documentNumber: "",
  documentDate: "",
  stockType: "",
  actualQuantity: "",
  parValueTotal: "",
  actualIssuePrice: "",
  totalAmount: "",
  unsoldQuantity: "",
  unsoldParValue: "",
});

// Initial values matching CreateStockLedgerDto và Schema Backend
const initialValues = {
  unit: "",
  address: "",
  year: today.getFullYear().toString(),
  measurementUnit: "",
  licenceNumber: "",
  licenceDate: "",
  registeredStockType: "",
  registeredQuantity: "",
  registeredParValue: "",
  details: [createDetailDefault()], // Có sẵn 1 dòng init data
  openingDate: "",
  reportDate: "",
  ledgerClerk: "",
  chiefAccountant: "",
  legalRepresentative: "",
  totalPages: "1", // Mặc định là 1 theo schema backend
  // Phụ trợ UI chữ ký
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
};

function StockLedgerDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [getData, setGetData] = useState({});

  const [formInitials, setFormInitials] = useState(initialValues);

  // Load data từ API và map vào form UI state
  useEffect(() => {
    (async () => {
      try {
        let apiData = {};
        if (id) {
          const response = await StockLedgerService.getById(id);
          apiData = response.data ? response.data : response;
        } else {
          try {
            const response = await StockLedgerService.getInit();
            apiData = response.data ? response.data : response;
          } catch (e) {
            console.log("No init endpoint", e);
          }
        }

        const formatDateVN = (isoString) => {
          if (!isoString) return "";
          if (typeof isoString === "string" && isoString.includes("/")) return isoString;
          const d = new Date(isoString);
          if (isNaN(d.getTime())) return isoString;
          return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(
            2,
            "0"
          )}/${d.getFullYear()}`;
        };

        // Mapping chi tiết từ API
        let mappedDetails = (apiData.details || []).map((item, index) => {
          const isFirst = index === 0;
          return {
            licenceNumber: isFirst ? apiData.licenceNumber || "" : "",
            licenceDate: isFirst ? formatDateVN(apiData.licenceDate) : "",
            registeredStockType: isFirst ? apiData.registeredStockType || "" : "",
            registeredQuantity: isFirst ? apiData.registeredQuantity || "" : "",
            registeredParValue: isFirst ? apiData.registeredParValue || "" : "",
            documentNumber: item.documentNumber || "",
            documentDate: formatDateVN(item.documentDate),
            stockType: item.stockType || "",
            actualQuantity: item.actualQuantity || "",
            parValueTotal: item.parValueTotal || "",
            actualIssuePrice: item.actualIssuePrice || "",
            totalAmount: item.totalAmount || "",
            unsoldQuantity: item.unsoldQuantity || "",
            unsoldParValue: item.unsoldParValue || "",
          };
        });

        // Đảm bảo luôn có ít nhất 1 dòng để nhập liệu nếu API trả về rỗng (trường hợp tạo mới)
        if (mappedDetails.length === 0) {
          mappedDetails = [createDetailDefault()];
          
          // Nếu API có header (licenceNumber...) nhưng ko có details, gán header vào dòng mới này
          if (apiData.licenceNumber || apiData.registeredQuantity) {
            mappedDetails[0].licenceNumber = apiData.licenceNumber || "";
            mappedDetails[0].licenceDate = formatDateVN(apiData.licenceDate);
            mappedDetails[0].registeredStockType = apiData.registeredStockType || "";
            mappedDetails[0].registeredQuantity = apiData.registeredQuantity || "";
            mappedDetails[0].registeredParValue = apiData.registeredParValue || "";
          }
        }

        // Parse reportDate -> signingDay/Month/Year
        let sDay = initialValues.signingDay;
        let sMonth = initialValues.signingMonth;
        let sYear = initialValues.signingYear;
        const rDate = apiData.reportDate || "";
        if (rDate) {
          const parts = typeof rDate === "string" ? rDate.split("/") : [];
          if (parts.length === 3) {
            sDay = parts[0];
            sMonth = parts[1];
            sYear = parts[2];
          } else {
            const d = new Date(rDate);
            if (!isNaN(d.getTime())) {
              sDay = d.getDate().toString();
              sMonth = (d.getMonth() + 1).toString();
              sYear = d.getFullYear().toString();
            }
          }
        }

        const loadedData = {
          ...initialValues,
          unit: apiData.unit || apiData.company?.name || "",
          address: apiData.address || apiData.company?.address || "",
          year: apiData.year?.toString() || initialValues.year,
          measurementUnit: apiData.measurementUnit || "",
          licenceNumber: apiData.licenceNumber || "",
          licenceDate: formatDateVN(apiData.licenceDate),
          registeredStockType: apiData.registeredStockType || "",
          registeredQuantity: apiData.registeredQuantity || "",
          registeredParValue: apiData.registeredParValue || "",
          details: mappedDetails,
          openingDate: formatDateVN(apiData.openingDate),
          reportDate: rDate,
          signingDay: sDay,
          signingMonth: sMonth,
          signingYear: sYear,
          ledgerClerk: apiData.ledgerClerk || "",
          chiefAccountant: apiData.chiefAccountant || "",
          legalRepresentative: apiData.legalRepresentative || "",
          totalPages: apiData.totalPages != null ? apiData.totalPages.toString() : "1",
        };

        setFormInitials(loadedData);
        setGetData(apiData);
      } catch (error) {
        console.error("Lỗi load Sổ Chi Tiết Phát Hành Cổ Phiếu (S43-DN):", error);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const handleBack = () => navigation(-1);

  // Submit payload theo CreateStockLedgerDto
  const submitForm = async (fValues) => {
    try {
      const num = (val) => (val && val !== "" && !isNaN(val) ? Number(val) : 0);
      const str = (val) => (val != null ? val.toString() : "");

      const itemsFlat = fValues.details || [];

      // Helper chuẩn hóa Date sang string dd/mm/yyyy
      const parseDateVN = (dateStr) => {
        if (!dateStr) return "";
        const parts = dateStr.split("/");
        if (parts.length === 3) {
          return `${parts[0].padStart(2, "0")}/${parts[1].padStart(2, "0")}/${parts[2]}`;
        }
        const d = new Date(dateStr);
        if (!isNaN(d.getTime())) {
          return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(
            2,
            "0"
          )}/${d.getFullYear()}`;
        }
        return dateStr;
      };

      // Build details
      const details = itemsFlat.map((item) => ({
        documentNumber: item.documentNumber || "",
        documentDate: parseDateVN(item.documentDate),
        stockType: item.stockType || "",
        actualQuantity: num(item.actualQuantity),
        parValueTotal: num(item.parValueTotal),
        actualIssuePrice: num(item.actualIssuePrice),
        totalAmount: num(item.totalAmount),
        unsoldQuantity: num(item.unsoldQuantity),
        unsoldParValue: num(item.unsoldParValue),
      }));

      // Build reportDate
      const signD = fValues.signingDay;
      const signM = fValues.signingMonth;
      const signY = fValues.signingYear;
      let reportDate = "";
      if (signY && signM && signD) {
        reportDate = `${String(signD).padStart(2, "0")}/${String(signM).padStart(2, "0")}/${signY}`;
      }

      // Lấy thông tin đăng ký từ dòng đầu tiên của UI
      const firstRow = itemsFlat[0] || {};

      // Payload CreateStockLedgerDto chuẩn Schema Backend
      const payload = {
        unit: fValues.unit || "",
        address: fValues.address || "",
        year: fValues.year ? fValues.year.toString() : new Date().getFullYear().toString(),
        measurementUnit: fValues.measurementUnit || "",
        
        // Header lấy từ dòng 1
        licenceNumber: firstRow.licenceNumber || "",
        licenceDate: parseDateVN(firstRow.licenceDate),
        registeredStockType: firstRow.registeredStockType || "",
        registeredQuantity: num(firstRow.registeredQuantity),
        registeredParValue: num(firstRow.registeredParValue),

        details,
        openingDate: parseDateVN(fValues.openingDate),
        reportDate,
        ledgerClerk: fValues.ledgerClerk || "",
        chiefAccountant: fValues.chiefAccountant || "",
        legalRepresentative: fValues.legalRepresentative || "",
        totalPages: str(fValues.totalPages) || "1",
      };

      console.log("🚀 SUBMIT PAYLOAD:", payload);

      if (!id) {
        await StockLedgerService.create(payload);
        toast.success("Tạo Sổ Chi Tiết Phát Hành Cổ Phiếu thành công!");
      } else {
        await StockLedgerService.update(id, payload);
        toast.success("Cập nhật thành công!");
      }
    } catch (error) {
      console.error("Lỗi lưu:", error);
      toast.error(error?.response?.data?.message || "Lưu thất bại!");
      throw error;
    }
  };

  const handlePrintDirect = (pValues) => {
    const htmlContent = getPrintTemplate({
      headerInfo: pValues,
      details: pValues.details || [],
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>In Sổ Chi Tiết Phát Hành Cổ Phiếu</title>
          <style>
            @media print { @page { size: A4 landscape; margin: 1cm; } }
          </style>
        </head>
        <body onload="setTimeout(() => [window.print(), window.close()], 500);">${htmlContent}</body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleSubmit = async (sValues, actions) => {
    try {
      await submitForm(sValues);
      if (!id) navigation("/templates/stock-ledger");
    } catch (error) {
      // Error handled
    } finally {
      if (actions) {
        actions.setTouched({});
        actions.setSubmitting(false);
      }
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center" sx={{ height: "100%" }}>
          <Grid item xs={12} lg={12}>
            <Formik initialValues={formInitials} enableReinitialize={true} onSubmit={handleSubmit}>
              {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <SoftBox p={2}>
                      <SoftBox>
                        <StockLedgerInfo
                          formData={{
                            values,
                            touched,
                            errors,
                            getData,
                            containView,
                            setFieldValue,
                          }}
                        />

                        <SoftBox mt={10} width="100%" display="flex" justifyContent="space-between">
                          <SoftButton variant="gradient" color="light" onClick={handleBack}>
                            Trở về
                          </SoftButton>

                          {containView ? (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                variant="gradient"
                                color="success"
                                onClick={() => handlePrintDirect(values)}
                                type="button"
                              >
                                In Sổ Chi Tiết Phát Hành Cổ Phiếu (S43-DN)
                              </SoftButton>
                            </SoftBox>
                          ) : (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                disabled={isSubmitting}
                                type="submit"
                                variant="gradient"
                                color="dark"
                              >
                                {id ? "Lưu" : "Tạo mới"}
                              </SoftButton>
                            </SoftBox>
                          )}
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default StockLedgerDetail;
